import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { getSavedItems, saveItem, unsaveItem } from '../lib/savedContentService';

const SavedContext = createContext(null);

export function SavedProvider({ children }) {
  const { user, isGuest } = useAuth();
  const [savedItems, setSavedItems]   = useState([]);
  const [savedMap,   setSavedMap]     = useState(new Map()); // item_id → record
  const [loading,    setLoading]      = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    const { data } = await getSavedItems({ userId: user?.id, isGuest });
    const items = data || [];
    setSavedItems(items);
    setSavedMap(new Map(items.map(i => [i.item_id, i])));
    setLoading(false);
  }, [user, isGuest]);

  useEffect(() => { reload(); }, [reload]);

  const isSaved = useCallback((itemId) => savedMap.has(itemId), [savedMap]);

  const toggleSave = useCallback(async (item) => {
    if (savedMap.has(item.item_id)) {
      // Optimistic remove
      setSavedItems(prev => prev.filter(i => i.item_id !== item.item_id));
      setSavedMap(prev => { const m = new Map(prev); m.delete(item.item_id); return m; });
      await unsaveItem({ userId: user?.id, isGuest, itemId: item.item_id });
    } else {
      // Optimistic add
      const newItem = { ...item, saved_at: new Date().toISOString() };
      setSavedItems(prev => [newItem, ...prev]);
      setSavedMap(prev => new Map(prev).set(item.item_id, newItem));
      await saveItem({ userId: user?.id, isGuest, item });
    }
  }, [savedMap, user, isGuest]);

  return (
    <SavedContext.Provider value={{ savedItems, isSaved, toggleSave, loading, reload }}>
      {children}
    </SavedContext.Provider>
  );
}

export function useSaved() {
  const ctx = useContext(SavedContext);
  if (!ctx) throw new Error('useSaved must be used within SavedProvider');
  return ctx;
}
